import { StructureObjectDto as StructureObjectDtoOld } from '@/endpoints/models'
import { StructureDto, StructureObjectDto } from '@/rtkApi/mddApiSliceGenerated'
import { NativeMap } from '@/utils/collections'
import { hasStructureEditPermission } from '@/utils/locking'

import { State } from '.'

/**
 * Children/tree payloads often omit `structurePermissions`. Without this, they replace the
 * whole node and wipe ACL from a prior NODE_LOAD (v2 metadata), so Edit re-enables after F5.
 */
export const mergeStructureNodeFromBulkLoad = (
	existing: StructureDto | undefined,
	incoming: StructureDto,
): StructureDto => {
	if (existing == null) {
		return {
			...incoming,
			canEdit: hasStructureEditPermission(incoming),
		}
	}

	const base = { ...existing, ...incoming }

	if (incoming.structurePermissions !== undefined) {
		const perms = incoming.structurePermissions
		const merged = {
			...base,
			canEdit: Array.isArray(perms) && perms.includes('EDIT'),
		}
		return {
			...merged,
			canEdit: hasStructureEditPermission(merged),
		}
	}

	if (existing.structurePermissions === undefined) {
		return {
			...base,
			canEdit: hasStructureEditPermission(base),
		}
	}

	const perms = existing.structurePermissions
	const canEditFromAcl = Array.isArray(perms) && perms.includes('EDIT')

	const merged = {
		...base,
		structurePermissions: perms,
		canEdit: canEditFromAcl,
	}

	return {
		...merged,
		canEdit: hasStructureEditPermission(merged),
	}
}

export const removeDeletedNodes = (
	deletedNode: StructureDto,
	state: State,
): {
	children: NativeMap<number[]>
	nodes: NativeMap<StructureDto>
	removedIds: number[]
} => {
	const removedIds: number[] = []
	const parentId = deletedNode.parentStructureId as number

	const updatedNodes = { ...state.nodes }

	const updatedChildren: NativeMap<number[]> = {
		...state.children,
		[parentId]: (state.children[parentId] || []).filter(
			(x) => x !== deletedNode.id,
		),
	}

	const deleteNodeIncludingChildren = (id: number) => {
		delete updatedNodes[id]
		delete updatedChildren[id]
		removedIds.push(id)

		const children = state.children[id] || []
		children.forEach((childId) => deleteNodeIncludingChildren(childId))
	}

	deleteNodeIncludingChildren(deletedNode.id || 0)

	return {
		nodes: updatedNodes,
		children: updatedChildren,
		removedIds,
	}
}

/** ROOT has no ACL on `/structure/{id}/children`; refresh top-level systems instead. */
export const isRootStructureParent = (
	parentStructureId: number | undefined | null,
	nodes: NativeMap<StructureDto>,
): boolean => {
	if (parentStructureId == null) {
		return true
	}

	const parent = nodes[parentStructureId]
	if (parent?.type === StructureObjectDtoOld.TypeEnum.ROOT) {
		return true
	}

	const rootId = Object.values(nodes).find(
		(node) => node.type === StructureObjectDtoOld.TypeEnum.ROOT,
	)?.id

	return rootId != null && rootId === parentStructureId
}

/**
 * Whether it's safe to refresh `node.parentStructureId`'s children after an action on `node`.
 * The company ROOT has no ACL, so a non-admin's `GET /structure/{rootId}/children` call fails
 * even when they have full permission on `node` itself. Systems are direct children of that
 * root, so a SYSTEM node's parent is always skipped; other node types are only skipped when
 * their parent is known (cached) to be the ROOT.
 */
export const shouldRefreshParentChildren = (
	node: { parentStructureId?: number | null; type?: string },
	nodes: NativeMap<StructureDto>,
): boolean => {
	if (node.parentStructureId == null) return false
	if (node.type === StructureObjectDtoOld.TypeEnum.SYSTEM) return false
	return !isRootStructureParent(node.parentStructureId, nodes)
}

export const findSystemNodeId = (
	node: StructureDto,
	nodes: NativeMap<StructureDto>,
): number => {
	if (!node || !nodes || !node.id) return 0

	if (
		node.type === StructureObjectDtoOld.TypeEnum.SYSTEM ||
		!node.parentStructureId
	) {
		return node.id
	}

	const parent = nodes[node.parentStructureId]

	if (!parent) {
		return node.id
	}

	return findSystemNodeId(parent, nodes)
}

export const getParents = (
	node: StructureObjectDto,
	nodes: NativeMap<StructureObjectDto>,
	parentIds: number[],
): number[] => {
	if (
		node.parentStructureId === undefined ||
		node.parentStructureId === null ||
		nodes[node.parentStructureId] === undefined
	) {
		return parentIds
	}

	return getParents(
		nodes[node.parentStructureId] as StructureObjectDto,
		nodes,
		[...parentIds, node.parentStructureId],
	)
}
